// ============================================================
// Settings Hub Page
// ============================================================
// Centralized settings management for Academic Sessions, Student
// Attendance Settings, Staff Attendance Settings, and School info.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardActionArea, Avatar, Chip } from '@mui/material';
import { CalendarDays, UserCheck, ClipboardList, ChevronRight } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import { PageHeader } from '@/components/navigation/PageHeader';
import { PATHS } from '@/router/paths';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import { ROLES } from '@/constants/roles';
import useAuthStore from '@/store/authStore';

export function SettingsPage() {
  const navigate = useNavigate();
  const { can } = usePermission();
  const { user } = useAuth();
  const modules = useAuthStore((s) => s.modules);

  const settingsModule = React.useMemo(() => {
    if (!Array.isArray(modules)) return null;
    return (
      modules.find((m) => {
        const modData = m?.module || m;
        const modName = (modData?.name || modData?.id || '').toUpperCase();
        return modName === 'SETTINGS';
      }) || null
    );
  }, [modules]);

  const backendMenus = React.useMemo(() => {
    const rawMod = settingsModule?.module || settingsModule;
    return Array.isArray(rawMod?.menus) ? rawMod.menus : [];
  }, [settingsModule]);

  const settingsCards = [
    {
      id: 'academic-sessions',
      menuNames: ['ACADEMIC_SESSION_SETTINGS', 'ACADEMIC_SESSIONS'],
      title: 'Academic Sessions',
      description: 'Manage academic years, start & end dates, and set the current active session.',
      icon: CalendarDays,
      color: tokens.colors.primary.main,
      bg: tokens.colors.primary[50],
      path: PATHS.ACADEMIC_SESSIONS || '/settings/academic-session',
      permission: PERMISSIONS.ACADEMIC_SESSION_MANAGE,
    },
    {
      id: 'student-attendance-settings',
      menuNames: ['STUDENT_ATTENDANCE_SETTINGS'],
      title: 'Student Attendance Settings',
      description: 'Configure student attendance rules, default status, cutoff times, and scanner options.',
      icon: UserCheck,
      color: '#0ea5e9',
      bg: '#e0f2fe',
      path: PATHS.STUDENT_ATTENDANCE_SETTINGS,
      permission: PERMISSIONS.STUDENT_ATTENDANCE_SETTINGS,
    },
    {
      id: 'staff-attendance-settings',
      menuNames: ['ATTENDANCE_SETTINGS', 'STAFF_ATTENDANCE_SETTINGS'],
      title: 'Staff Attendance Settings',
      description: 'Manage staff working hours, grace period, late mark threshold, and attendance policies.',
      icon: ClipboardList,
      color: '#8b5cf6',
      bg: '#f3e8ff',
      path: PATHS.ATTENDANCE_SETTINGS,
      permission: PERMISSIONS.STAFF_ATTENDANCE_SETTINGS,
    },
  ];

  const visibleCards = React.useMemo(() => {
    if (backendMenus.length === 0) {
      return settingsCards.filter((card) => (card.permission ? can(card.permission) : true));
    }
    return settingsCards
      .filter((card) => {
        const matchesBackend = backendMenus.some((m) => {
          const mName = (m.name || m.id || '').toUpperCase();
          return card.menuNames.includes(mName) || m.route === card.path;
        });
        const matchesPermission = card.permission ? can(card.permission) : true;
        return matchesBackend && matchesPermission;
      })
      .map((card) => {
        const matchingMenu = backendMenus.find((m) => {
          const mName = (m.name || m.id || '').toUpperCase();
          return card.menuNames.includes(mName) || m.route === card.path;
        });
        return {
          ...card,
          path: matchingMenu?.route || card.path,
        };
      });
  }, [backendMenus, can]);

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Settings & Configuration"
        subtitle="Manage school settings, academic sessions, and attendance policies."
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
        {visibleCards.map((card) => {
          const Icon = card.icon;
          const isAccessible = card.permission ? can(card.permission) : true;

          return (
            <Card
              key={card.id}
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: tokens.radius.card,
                border: `1px solid ${tokens.colors.border}`,
                transition: 'all 0.2s ease-in-out',
                opacity: isAccessible ? 1 : 0.6,
                '&:hover': isAccessible
                  ? {
                    borderColor: tokens.colors.primary.light,
                    boxShadow: tokens.shadows.sm,
                    transform: 'translateX(3px)',
                  }
                  : {},
              }}
            >
              <CardActionArea
                onClick={() => isAccessible && navigate(card.path)}
                disabled={!isAccessible}
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: card.bg,
                      color: card.color,
                      width: 48,
                      height: 48,
                      borderRadius: tokens.radius.md,
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={24} />
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ fontSize: '1.05rem', color: tokens.colors.text.primary, mb: 0.3 }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4 }}
                    >
                      {card.description}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 2, flexShrink: 0 }}>
                  {!isAccessible && (
                    <Chip
                      label="Restricted"
                      size="small"
                      color="default"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                  {isAccessible && (
                    <ChevronRight size={22} color={tokens.colors.text.muted} />
                  )}
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
